import axios from 'axios';
import config from '../config';
import authService from './authService';

// Create axios instance for Notifications API
const notificationsApi = axios.create({
    baseURL: `${config.API_BASE_URL}${config.NOTIFICATIONS_API_PREFIX}/${config.NOTIFICATIONS_API_VERSION}`,
    timeout: config.TIMEOUTS.API_REQUEST,
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
    }
});

// Request interceptor: add access token
notificationsApi.interceptors.request.use(
    async (config) => {
        const token = await authService.getValidAccessToken();
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// Response interceptor: handle 401
notificationsApi.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        if (error.response?.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;
            try {
                const newToken = await authService.getValidAccessToken();
                if (newToken) {
                    originalRequest.headers.Authorization = `Bearer ${newToken}`;
                    return notificationsApi(originalRequest);
                } else {
                    authService.forceLogout();
                    return Promise.reject(new Error('Authentication failed'));
                }
            } catch (refreshError) {
                console.error('Token refresh failed:', refreshError);
                authService.forceLogout();
                return Promise.reject(refreshError);
            }
        }
        return Promise.reject(error.response?.data || error.message);
    }
);

const NotificationsService = {
    // SMTP Setup methods
    getSmtpById: async (configData) => {
        try {
            const payload = {
                ...configData
            };
            const response = await notificationsApi.post(config.NOTIFICATIONS.ENDPOINTS.SMTP.GET_BY_ID, payload);
            return response.data;
        } catch (error) {
            throw error.message;
        }
    },

    saveSmtpSetup: async (configData) => {
        try {
            const payload = {
                ...configData
            };
            const response = await notificationsApi.post(config.NOTIFICATIONS.ENDPOINTS.SMTP.SAVE, payload);
            return response.data;
        } catch (error) {
            throw error.message;
        }
    },

    deleteSmtpSetup: async (configData) => {
        try {
            const payload = {
                ...configData
            };
            const response = await notificationsApi.post(
                config.NOTIFICATIONS.ENDPOINTS.SMTP.DELETE,
                payload
            );
            return response.data;
        } catch (error) {
            throw error.message;
        }
    },

    getSmtpSetups: async (filters = {}) => {
        try {
            const listFilters = {};
            const response = await notificationsApi.get(
                config.NOTIFICATIONS.ENDPOINTS.SMTP.GET_LIST,
                listFilters
            );
            return response.data;
        } catch (error) {
            throw error.message;
        }
    },

    // Notification Template methods
    getTemplateById: async (configData) => {
        try {
            const payload = {
                ...configData
            };
            const response = await notificationsApi.post(config.NOTIFICATIONS.ENDPOINTS.TEMPLATES.GET_BY_ID, payload);
            return response.data;
        } catch (error) {
            throw error.message;
        }
    },

    saveTemplate: async (configData) => {
        try {
            const payload = {
                ...configData
            };
            const response = await notificationsApi.post(config.NOTIFICATIONS.ENDPOINTS.TEMPLATES.SAVE, payload);
            return response.data;
        } catch (error) {
            throw error.message;
        }
    },

    deleteTemplate: async (configData) => {
        try {
            const payload = {
                ...configData
            };
            const response = await notificationsApi.post(
                config.NOTIFICATIONS.ENDPOINTS.TEMPLATES.DELETE,
                payload
            );
            return response.data;
        } catch (error) {
            throw error.message;
        }
    },

    getTemplates: async (filters = {}) => {
        try {
            const listFilters = {};
            const response = await notificationsApi.post(
                config.NOTIFICATIONS.ENDPOINTS.TEMPLATES.GET_LIST,
                listFilters
            );
            return response.data;
        } catch (error) {
            throw error.message;
        }
    },

    // Enum methods
    getNotificationPlatforms: async () => {
        try {
            const response = await notificationsApi.get(config.NOTIFICATIONS.ENDPOINTS.TEMPLATES.GET_NOTIFICATION_PLATFORMS);
            return response.data;
        } catch (error) {
            throw error.message;
        }
    },

    getNotificationCategories: async () => {
        try {
            const response = await notificationsApi.get(config.NOTIFICATIONS.ENDPOINTS.TEMPLATES.GET_NOTIFICATION_CATEGORIES);
            return response.data;
        } catch (error) {
            throw error.message;
        }
    },

    getNotificationTemplateApplications: async () => {
        try {
            const response = await notificationsApi.get(config.NOTIFICATIONS.ENDPOINTS.TEMPLATES.GET_NOTIFICATION_TEMPLATE_APPLICATIONS);
            return response.data;
        } catch (error) {
            throw error.message;
        }
    },

    getNotificationTypes: async () => {
        try {
            const response = await notificationsApi.get(config.NOTIFICATIONS.ENDPOINTS.TEMPLATES.GET_NOTIFICATION_TYPES);
            return response.data;
        } catch (error) {
            throw error.message;
        }
    },
    getReservedWords: async () => {
        try {
            const response = await notificationsApi.get(config.NOTIFICATIONS.ENDPOINTS.TEMPLATES.GET_RESERVED_WORDS);
            return response.data;
        } catch (error) {
            throw error.message;
        }
    },
};

export default NotificationsService;